import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signInWithCustomToken } from 'firebase/auth';
import { Star, Mail, Smile } from 'lucide-react';
import { auth } from '@/shared/api/firebase_client';
import { useAuth } from '@/features/auth';
import { post } from '@/shared/api/api_client';
import { Button, Input, Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import AuthByGoogle from '@/features/auth/AuthByGoogle';

// ---------------------------------------------------------------------------
// Validation schemas
// ---------------------------------------------------------------------------
const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const forgotSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
});

type ForgotFormValues = z.infer<typeof forgotSchema>;

const childLoginSchema = z.object({
  familyCode: z.string().min(1, 'Family code is required').max(12, 'Invalid family code'),
  firstName: z.string().min(1, 'First name is required').max(50, 'First name too long'),
  pin: z
    .string()
    .min(4, 'PIN must be 4–6 digits')
    .max(6, 'PIN must be 4–6 digits')
    .regex(/^\d+$/, 'PIN must be numeric'),
});

type ChildLoginFormValues = z.infer<typeof childLoginSchema>;

// ---------------------------------------------------------------------------
// API response type for child login
// ---------------------------------------------------------------------------
interface ChildLoginResponse {
  customToken: string;
  uid: string;
  memberId: string;
  familyId: string;
  displayName: string;
}

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------
type LoginTab = 'parent' | 'child';

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------
export default function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';
  const { signIn } = useAuth();

  const [activeTab, setActiveTab] = useState<LoginTab>('parent');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);

  // Parent email/password form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  // Forgot password form
  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors },
  } = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
  });

  // Child login form
  const {
    register: registerChild,
    handleSubmit: handleChildSubmit,
    formState: { errors: childErrors, isSubmitting: childSubmitting },
    setError: setChildError,
  } = useForm<ChildLoginFormValues>({
    resolver: zodResolver(childLoginSchema),
  });

  // Email/password sign in
  const onSubmit = async (data: LoginFormValues) => {
    try {
      await signIn(data.email, data.password);
      navigate(redirectTo);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Invalid email or password.';
      setError('root', { message });
    }
  };

  // Forgot password
  const onForgotSubmit = async (data: ForgotFormValues) => {
    setForgotError(null);
    setForgotLoading(true);
    try {
      await post<void>('/auth/password-reset', { email: data.email });
      setForgotSuccess(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setForgotError(message);
    } finally {
      setForgotLoading(false);
    }
  };

  // Child PIN login
  const onChildSubmit = async (data: ChildLoginFormValues) => {
    try {
      const res = await post<ChildLoginResponse>('/auth/child-login', {
        familyCode: data.familyCode.trim().toUpperCase(),
        firstName: data.firstName.trim(),
        pin: data.pin,
      });
      await signInWithCustomToken(auth, res.customToken);
      navigate(redirectTo);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      // Detect lockout message from server
      const lockoutMatch = /try again in (\d+ minutes?)/i.exec(message);
      if (lockoutMatch) {
        setChildError('root', {
          message: `Too many attempts. Please try again in ${lockoutMatch[1]}.`,
        });
      } else {
        setChildError('root', { message: 'Incorrect username or PIN.' });
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12">
      {/* Logo + tagline */}
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <div className="flex items-center gap-2 text-primary-700">
          <Star size={32} className="text-accent-500 fill-accent-400" aria-hidden="true" />
          <span className="text-3xl font-extrabold tracking-tight">KidKred</span>
        </div>
        <p className="text-base text-slate-500">Family chores & rewards, made fun.</p>
      </div>

      <Card className="w-full max-w-sm" shadow="md">
        {/* Tab switcher */}
        <div className="mb-6 flex gap-2">
          <button
            type="button"
            onClick={() => { setActiveTab('parent'); setShowForgot(false); }}
            className={cn(
              'flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
              activeTab === 'parent'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            )}
            aria-pressed={activeTab === 'parent'}
          >
            I'm a Parent
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('child'); setShowForgot(false); }}
            className={cn(
              'flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-1',
              activeTab === 'child'
                ? 'bg-secondary-500 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            )}
            aria-pressed={activeTab === 'child'}
          >
            I'm a Kid
          </button>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* PARENT TAB                                                          */}
        {/* ------------------------------------------------------------------ */}
        {activeTab === 'parent' && (
          <>
            <h1 className="mb-6 text-xl font-bold text-slate-900 text-center">
              Sign in to KidKred
            </h1>

            {showForgot ? (
              <div className="flex flex-col gap-4">
                {forgotSuccess ? (
                  <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-4 text-center">
                    <p className="text-sm font-semibold text-secondary-700">Check your email!</p>
                    <p className="mt-1 text-xs text-secondary-600">
                      We've sent a password reset link if that email is registered.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleForgotSubmit(onForgotSubmit)}
                    noValidate
                    className="flex flex-col gap-4"
                  >
                    <p className="text-sm text-slate-600">
                      Enter your email and we'll send you a reset link.
                    </p>
                    <Input
                      label="Email address"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      error={forgotErrors.email?.message}
                      {...registerForgot('email')}
                    />
                    {forgotError && (
                      <p role="alert" className="text-xs text-danger-600">
                        {forgotError}
                      </p>
                    )}
                    <Button type="submit" isLoading={forgotLoading} fullWidth>
                      Send reset link
                    </Button>
                  </form>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowForgot(false);
                    setForgotSuccess(false);
                    setForgotError(null);
                  }}
                  className="text-center text-sm text-primary-600 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                >
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {/* Google sign in — uses redirect flow to avoid COOP issues */}
                <AuthByGoogle onSuccess={() => navigate(redirectTo)} />

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-slate-200" />
                  <span className="text-xs text-slate-400">or</span>
                  <div className="flex-1 border-t border-slate-200" />
                </div>

                {/* Email/password form */}
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="flex flex-col gap-4"
                >
                  <Input
                    label="Email address"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    leftIcon={<Mail size={16} />}
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <div className="flex flex-col gap-1">
                    <Input
                      label="Password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      error={errors.password?.message}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className={cn(
                        'self-end text-xs text-primary-600 hover:text-primary-800',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded',
                      )}
                    >
                      Forgot password?
                    </button>
                  </div>

                  {errors.root && (
                    <p role="alert" className="text-xs text-danger-600">
                      {errors.root.message}
                    </p>
                  )}

                  <Button type="submit" isLoading={isSubmitting} fullWidth size="lg">
                    Sign in
                  </Button>
                </form>
              </div>
            )}

            {!showForgot && (
              <p className="mt-6 text-center text-sm text-slate-500">
                New family?{' '}
                <span className="font-medium text-primary-600">
                  Start your free trial
                </span>
                {' '}— coming soon!
              </p>
            )}
          </>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* CHILD TAB                                                           */}
        {/* ------------------------------------------------------------------ */}
        {activeTab === 'child' && (
          <div className="flex flex-col items-center gap-5">
            {/* Fun header */}
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100">
                <Smile size={32} className="text-secondary-600" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900">Hey there!</h1>
              <p className="text-base text-slate-500">Let's get you signed in.</p>
            </div>

            <form
              onSubmit={handleChildSubmit(onChildSubmit)}
              noValidate
              className="flex w-full flex-col gap-5"
            >
              {/* Family Code */}
              <div className="flex flex-col gap-1">
                <label className="text-base font-semibold text-slate-700">
                  Family Code
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  placeholder="e.g. SMITH7KN"
                  aria-invalid={childErrors.familyCode ? 'true' : undefined}
                  className={cn(
                    'block w-full rounded-xl border bg-white px-4 py-3 text-lg uppercase tracking-widest text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal placeholder:normal-case',
                    'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                    childErrors.familyCode
                      ? 'border-danger-400 focus:ring-danger-400'
                      : 'border-slate-300 focus:ring-secondary-500 focus:border-secondary-500',
                  )}
                  {...registerChild('familyCode')}
                />
                {childErrors.familyCode && (
                  <p role="alert" className="text-xs text-danger-600">
                    {childErrors.familyCode.message}
                  </p>
                )}
              </div>

              {/* First Name */}
              <div className="flex flex-col gap-1">
                <label className="text-base font-semibold text-slate-700">
                  Your first name
                </label>
                <input
                  type="text"
                  autoComplete="given-name"
                  placeholder="e.g. Emma"
                  aria-invalid={childErrors.firstName ? 'true' : undefined}
                  className={cn(
                    'block w-full rounded-xl border bg-white px-4 py-3 text-lg text-slate-900 placeholder:text-slate-300',
                    'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                    childErrors.firstName
                      ? 'border-danger-400 focus:ring-danger-400'
                      : 'border-slate-300 focus:ring-secondary-500 focus:border-secondary-500',
                  )}
                  {...registerChild('firstName')}
                />
                {childErrors.firstName && (
                  <p role="alert" className="text-xs text-danger-600">
                    {childErrors.firstName.message}
                  </p>
                )}
              </div>

              {/* PIN */}
              <div className="flex flex-col gap-1">
                <label className="text-base font-semibold text-slate-700">
                  Your PIN
                </label>
                <div className="relative">
                  <input
                    type={showPin ? 'text' : 'password'}
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="current-password"
                    placeholder="••••••"
                    aria-invalid={childErrors.pin ? 'true' : undefined}
                    className={cn(
                      'block w-full rounded-xl border bg-white px-4 py-3 pr-12 text-2xl tracking-[0.5em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal',
                      'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150',
                      childErrors.pin
                        ? 'border-danger-400 focus:ring-danger-400'
                        : 'border-slate-300 focus:ring-secondary-500 focus:border-secondary-500',
                    )}
                    {...registerChild('pin')}
                  />
                  <button
                    type="button"
                    aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                    onClick={() => setShowPin((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPin ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={20}
                        height={20}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={20}
                        height={20}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {childErrors.pin && (
                  <p role="alert" className="text-xs text-danger-600">
                    {childErrors.pin.message}
                  </p>
                )}
              </div>

              {/* Root error (auth failure / lockout) */}
              {childErrors.root && (
                <div className="rounded-lg bg-danger-50 border border-danger-200 px-3 py-2">
                  <p role="alert" className="text-sm text-danger-700">
                    {childErrors.root.message}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={childSubmitting}
                aria-busy={childSubmitting}
                className={cn(
                  'w-full rounded-xl py-3.5 text-lg font-bold text-white',
                  'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2',
                  'transition-colors duration-150 select-none',
                  'disabled:bg-secondary-300 disabled:cursor-not-allowed',
                )}
              >
                {childSubmitting ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          </div>
        )}
      </Card>
    </div>
  );
}
